import { LS } from './storage';
import { applyBrand } from './brand';

export const DARK_THEME = {
  bg: "#08080d", card: "rgba(255,255,255,0.04)", cardBorder: "rgba(255,255,255,0.07)",
  accent: "#00f5a0", accent2: "#00d9f5", warn: "#ff9f43", danger: "#ff6b6b", purple: "#a78bfa",
  text: "#eeeef4", text2: "#9595a8", text3: "#52526a", font: "'Outfit', sans-serif", mono: "'JetBrains Mono', monospace",
  sheetBg: "linear-gradient(180deg,#16161f,#08080d)", navBg: "rgba(10,10,16,0.92)", mode: "dark",
  // ── Visual depth tokens ──
  cardGrad: "linear-gradient(165deg, rgba(255,255,255,0.055) 0%, rgba(255,255,255,0.02) 100%)",
  cardGlow: "linear-gradient(165deg, rgba(0,245,160,0.09) 0%, rgba(0,217,245,0.03) 100%)",
  inputBg: "rgba(255,255,255,0.045)",
  hoverBg: "rgba(255,255,255,0.05)",
  divider: "rgba(255,255,255,0.05)",
  shadow: "0 1px 2px rgba(0,0,0,0.25), 0 8px 24px -12px rgba(0,0,0,0.5)",
  shadowLg: "0 24px 64px -16px rgba(0,0,0,0.65), 0 4px 16px rgba(0,0,0,0.35)",
  glow: "0 4px 20px -4px rgba(0,245,160,0.45)",
  ambient:
    "radial-gradient(ellipse 70% 45% at 18% -8%, rgba(0,245,160,0.085), transparent 60%)," +
    "radial-gradient(ellipse 60% 40% at 95% 4%, rgba(0,217,245,0.06), transparent 55%)," +
    "radial-gradient(ellipse 75% 55% at 50% 115%, rgba(167,139,250,0.055), transparent 60%)",
};
export const LIGHT_THEME = {
  bg: "#f4f5f8", card: "rgba(255,255,255,0.85)", cardBorder: "rgba(15,23,42,0.08)",
  accent: "#059669", accent2: "#0891b2", warn: "#d97706", danger: "#dc2626", purple: "#7c3aed",
  text: "#16162a", text2: "#4c4c64", text3: "#8b8b9e", font: "'Outfit', sans-serif", mono: "'JetBrains Mono', monospace",
  sheetBg: "linear-gradient(180deg,#ffffff,#f1f2f6)", navBg: "rgba(250,250,253,0.92)", mode: "light",
  // ── Visual depth tokens ──
  cardGrad: "linear-gradient(165deg, #ffffff 0%, #fbfbfd 100%)",
  cardGlow: "linear-gradient(165deg, rgba(5,150,105,0.08) 0%, rgba(8,145,178,0.03) 100%)",
  inputBg: "rgba(15,23,42,0.04)",
  hoverBg: "rgba(15,23,42,0.04)",
  divider: "rgba(15,23,42,0.06)",
  shadow: "0 1px 2px rgba(16,24,40,0.05), 0 8px 24px -12px rgba(16,24,40,0.12)",
  shadowLg: "0 24px 64px -16px rgba(16,24,40,0.25), 0 4px 16px rgba(16,24,40,0.08)",
  glow: "0 4px 20px -4px rgba(5,150,105,0.35)",
  ambient:
    "radial-gradient(ellipse 70% 45% at 18% -8%, rgba(5,150,105,0.07), transparent 60%)," +
    "radial-gradient(ellipse 60% 40% at 95% 4%, rgba(8,145,178,0.06), transparent 55%)," +
    "radial-gradient(ellipse 75% 55% at 50% 115%, rgba(124,58,237,0.05), transparent 60%)",
};

// Resolve initial mode: stored pref → OS preference → dark default
const _initialMode = () => {
  const stored = LS.get("ft-theme");
  if (stored === "light" || stored === "dark") return stored;
  if (typeof window !== "undefined" && window.matchMedia) {
    return window.matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark";
  }
  return "dark";
};

const _base = _initialMode()==="light" ? LIGHT_THEME : DARK_THEME;
// `brand` (org branding or null) and `onAccent` (foreground on the accent) are
// owned by utils/brand.js; the defaults reproduce today's solo look exactly.
export const V = {..._base, brand: null, onAccent: _base.mode === "light" ? "#fff" : _base.bg};

// Mirror key tokens into CSS custom properties so index.css stays theme-aware
export const syncCssVars = (t) => {
  if (typeof document === "undefined") return;
  const r = document.documentElement;
  r.dataset.theme = t.mode;
  r.style.setProperty("--bg", t.bg);
  r.style.setProperty("--text", t.text);
  r.style.setProperty("--text2", t.text2);
  r.style.setProperty("--accent", t.accent);
  r.style.setProperty("--accent2", t.accent2);
  r.style.setProperty("--card-border", t.cardBorder);
  r.style.setProperty("--on-accent", t.onAccent || (t.mode === "light" ? "#fff" : t.bg));
};
syncCssVars(V);

// Subscribers — let consumers re-render on theme change without rebuilding the tree
const _subs = new Set();
export const onThemeChange = (fn) => { _subs.add(fn); return () => _subs.delete(fn); };
export const notifyThemeChange = (mode) => { _subs.forEach(fn => { try { fn(mode); } catch {} }); };

// Switch light/dark. The brand (if any) is re-applied on top of the new base so
// the toggle keeps the org's colours; applyBrand(null) is byte-identical to the
// old Object.assign(V, theme) path for solo users.
// `persist:false` applies the mode without recording it as the user's choice (System mode).
export const setTheme = (mode, { persist = true } = {}) => {
  const t = mode === "light" ? LIGHT_THEME : DARK_THEME;
  Object.assign(V, t);
  if (persist) LS.set("ft-theme", mode);
  applyBrand(V.brand || null);
};

const _osMode = () => (typeof window !== "undefined" && window.matchMedia
  && window.matchMedia("(prefers-color-scheme: light)").matches) ? "light" : "dark";
// "System": forget the stored choice and follow the OS preference from now on
export const followSystemTheme = () => { LS.del("ft-theme"); setTheme(_osMode(), { persist: false }); };
export const themeChoice = () => { const t = LS.get("ft-theme"); return t === "light" || t === "dark" ? t : "system"; };

// React to OS preference changes when the user hasn't explicitly chosen
if (typeof window !== "undefined" && window.matchMedia) {
  try {
    const mq = window.matchMedia("(prefers-color-scheme: light)");
    const onChange = (e) => {
      // Not persisted, so the app keeps following the OS on later changes too
      if (LS.get("ft-theme") == null) setTheme(e.matches ? "light" : "dark", { persist: false });
    };
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else if (mq.addListener) mq.addListener(onChange);
  } catch {}
}

// ─── Haptic Feedback ───
export const Haptic = {
  light: () => { try { navigator.vibrate?.(10); } catch(e) {} },
  medium: () => { try { navigator.vibrate?.(25); } catch(e) {} },
  heavy: () => { try { navigator.vibrate?.([30, 20, 50]); } catch(e) {} },
  success: () => { try { navigator.vibrate?.([10, 30, 10, 30, 50]); } catch(e) {} },
};
